import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import InfoCard, { InfoCardAction } from '../InfoCard'
import styles from './styles.module.scss'

// Matches the out animation in styles.module.scss
const CLOSE_MS = 220

interface InfoModalProps {
    open: boolean
    onClose: () => void
    // For aria-labelledby; unique per modal
    titleId: string
    // Without a title, label names the dialog for screen readers instead
    title?: React.ReactNode
    label?: string
    children: React.ReactNode
    // Graphic up top; the orb unless given
    visual?: React.ReactNode
    // An extra button above Close (e.g. "Visit site")
    primaryAction?: InfoCardAction
}

/**
 * An InfoCard in the middle of the screen over a dimmed, blurred backdrop,
 * always with a Close button. Closes on Close, Esc or a click on the backdrop,
 * animating out before it unmounts. Focus starts on Close and returns to
 * whatever opened it.
 */
const InfoModal: React.FC<InfoModalProps> = ({
    open,
    onClose,
    titleId,
    title,
    label,
    children,
    visual,
    primaryAction,
}) => {
    // Kept mounted through the out animation
    const [rendered, setRendered] = useState(open)
    const [closing, setClosing] = useState(false)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (open) {
            setRendered(true)
            setClosing(false)
            return
        }
        if (!rendered) return
        setClosing(true)
        const timer = setTimeout(() => setRendered(false), CLOSE_MS)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // While open: Esc closes, the page behind doesn't scroll, and focus starts on
    // Close and returns afterwards
    useEffect(() => {
        // Waits for the card to be on the page, so Close can take focus
        if (!open || !rendered) return
        const returnFocus = document.activeElement as HTMLElement | null
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
        const overflow = document.body.style.overflow
        document.addEventListener('keydown', onKey)
        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = overflow
            returnFocus?.focus()
        }
    }, [open, rendered, onClose])

    if (!rendered) return null

    const close: InfoCardAction = { label: 'Close', onClick: onClose, ref: closeRef }

    return createPortal(
        <div className={`${styles.backdrop} ${closing ? styles.closing : ''}`} onClick={onClose}>
            <InfoCard
                className={closing ? styles.cardOut : ''}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-label={title ? undefined : label}
                titleId={titleId}
                title={title}
                visual={visual}
                action={primaryAction ?? close}
                secondaryAction={primaryAction ? close : undefined}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </InfoCard>
        </div>,
        document.body,
    )
}

export default InfoModal
